/** Range Planning "Attributes & Allergens" governance list (Demo Mode only). Central
 * registry of every allergen/dietary attribute code used across Recipes and Items, plus a
 * catalogue-wide Conflicts view (see attributesAllergensClient.getConflicts). */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Chip,
  Grid,
  MenuItem,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon, Search } from '@mui/icons-material';
import PageArea from '../../components/shared/PageArea';
import PageTitle from '../../components/shared/PageTitle';
import { Section } from '../../components/ui/Section';
import { TableHeader } from '../../components/forms/TableHeader';
import { Pill } from '../../components/shared/Pill';
import CustomPagination from '../../components/forms/Pagination';
import useAttributesList from './hooks/useAttributesList';
import useConflicts from './hooks/useConflicts';
import { AttributeGovernanceStatus, DietaryCategory } from './types';

const statusColor: Record<AttributeGovernanceStatus, 'success' | 'warning' | 'default'> = {
  [AttributeGovernanceStatus.Active]: 'success',
  [AttributeGovernanceStatus.UnderReview]: 'warning',
  [AttributeGovernanceStatus.Deprecated]: 'default',
};

const PAGE_SIZE = 20;

enum ListTab {
  Registry = 'registry',
  Conflicts = 'conflicts',
}

const AttributesAllergensList = () => {
  const { t } = useTranslation('attributesAllergens');
  const navigate = useNavigate();
  const { attributes, totalCount, isLoading, loadAttributes } = useAttributesList();
  const { conflicts, isLoading: conflictsLoading, loadConflicts } = useConflicts();

  const [tabValue, setTabValue] = useState<ListTab>(ListTab.Registry);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [page, setPage] = useState(0);
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);

  const handleClearFilters = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCategory('');
    setSearchInput('');
    setSearch('');
    setPage(0);
  };

  useEffect(() => {
    loadAttributes(search, category, page, PAGE_SIZE);
  }, [loadAttributes, search, category, page]);

  useEffect(() => {
    if (tabValue === ListTab.Conflicts) loadConflicts();
  }, [tabValue, loadConflicts]);

  const maxPages = useMemo(() => Math.max(1, Math.ceil(totalCount / PAGE_SIZE)), [totalCount]);

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(0);
  };

  return (
    <PageArea>
      <PageTitle>{t('title', 'Attributes & Allergens')}</PageTitle>

      <Typography variant="textSM"
        color="text.secondary"
        sx={{ mt: 2, mb: 4 }}>
        {t('intro', 'Central governance registry for allergen and dietary attribute codes, with traceability across Recipes and Items and automatic conflict detection.')}
      </Typography>

      <Tabs value={tabValue}
        onChange={(_, v) => setTabValue(v)}
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 4 }}>
        <Tab label={t('registry', 'Registry')}
          value={ListTab.Registry} />
        <Tab label={t('conflicts', 'Conflicts')}
          value={ListTab.Conflicts} />
      </Tabs>

      {tabValue === ListTab.Registry && (
        <>
          {/* Quick filter + search row */}
          <Grid container
            spacing={2}
            alignItems="center"
            sx={{ mb: 2 }}>
            <Grid item
              sx={{ display: 'flex', alignItems: 'center' }}>
              <TextField
                size="small"
                label={t('search', 'Search')}
                placeholder={t('searchPlaceholder', 'Search by code or name')}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                onBlur={handleSearch}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
                sx={{ minWidth: 260 }}
              />
            </Grid>
          </Grid>

          {/* Advanced filters accordion */}
          <Accordion
            variant="borderless"
            expanded={isAdvancedOpen}
            onChange={(_, expanded) => setIsAdvancedOpen(expanded)}
            sx={{ mb: 4 }}
          >
            <AccordionSummary>
              <Grid container
                alignItems="center"
                justifyContent="space-between"
                sx={{ width: '100%' }}>
                <Grid item>
                  <Grid container
                    spacing={1}
                    alignItems="center">
                    <Grid item>
                      {t('filters.advanced', 'Advanced filters')}
                    </Grid>
                    <Grid item
                      style={{ marginTop: '8px' }}>
                      {isAdvancedOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item>
                  <Button
                    size="md"
                    variant="secondary"
                    onClick={handleClearFilters}
                  >
                    {t('filters.clear', 'Clear filters')}
                  </Button>
                </Grid>
              </Grid>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container
                spacing={3}>
                <Grid item
                  lg={3}
                  sm={6}
                  xs={12}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label={t('categoryLabel', 'Category')}
                    value={category}
                    onChange={(e) => { setCategory(e.target.value); setPage(0); }}
                  >
                    <MenuItem value="">{t('allCategories', 'All categories')}</MenuItem>
                    {Object.values(DietaryCategory).map((c) => (
                      <MenuItem key={c}
                        value={c}>{t(`category.${c}`, c)}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          <Section>
            <TableHeader>
              {t('registry', 'Registry')}
              <Pill text={`${totalCount} ${t('attributes', 'Attributes')}`} />
            </TableHeader>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('code', 'Code')}</TableCell>
                    <TableCell>{t('name', 'Name')}</TableCell>
                    <TableCell>{t('categoryLabel', 'Category')}</TableCell>
                    <TableCell>{t('regulatory', 'Regulatory')}</TableCell>
                    <TableCell>{t('governanceStatus', 'Status')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!isLoading && attributes.length === 0 && (
                    <TableRow><TableCell colSpan={5}>{t('noAttributes', 'No attributes match this filter.')}</TableCell></TableRow>
                  )}
                  {attributes.map((attr) => (
                    <TableRow
                      key={attr.code}
                      hover
                      onClick={() => navigate(`/range-planning/attributes-allergens/${attr.code}`)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>{attr.code}</TableCell>
                      <TableCell>{attr.name}</TableCell>
                      <TableCell>{t(`category.${attr.category}`, attr.category)}</TableCell>
                      <TableCell>{attr.isRegulatory ? t('yes', 'Yes') : t('no', 'No')}</TableCell>
                      <TableCell>
                        <Chip label={t(`governance.${attr.governanceStatus}`, attr.governanceStatus)}
                          color={statusColor[attr.governanceStatus]}
                          size="small" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {!isLoading && (
              <CustomPagination
                page={page}
                setPage={setPage}
                maxPages={maxPages}
                breakpointForChangeDisplay={120000}
                useNoDataMessage={false}
              />
            )}
          </Section>
        </>
      )}

      {tabValue === ListTab.Conflicts && (
        <Section>
          {!conflictsLoading && conflicts.length === 0 && (
            <Alert severity="success">{t('noConflicts', 'No conflicts detected across the current catalogue.')}</Alert>
          )}
          {conflicts.length > 0 && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('entity', 'Entity')}</TableCell>
                  <TableCell>{t('attributeCode', 'Claim')}</TableCell>
                  <TableCell>{t('conflictingAttributeCode', 'Conflicts With')}</TableCell>
                  <TableCell>{t('description', 'Description')}</TableCell>
                  <TableCell>{t('severity', 'Severity')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {conflicts.map((conflict) => (
                  <TableRow key={conflict.id}>
                    <TableCell>{conflict.entityName}</TableCell>
                    <TableCell>{conflict.attributeCode}</TableCell>
                    <TableCell>{conflict.conflictingAttributeCode}</TableCell>
                    <TableCell>{conflict.description}</TableCell>
                    <TableCell><Chip label={conflict.severity}
                      color="error"
                      size="small" /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Section>
      )}
    </PageArea>
  );
};

export default AttributesAllergensList;
